<script setup lang="ts">
import { LoaderCircle } from 'lucide-vue-next'
import { computed } from 'vue'

import { cn, CONTROL_HEIGHT, CONTROL_PADDING, ICON_STROKE } from './classes'
import type { ControlSize } from './types'

/**
 * Tlačidlo.
 *
 * `primary` je jediné miesto (spolu s aktívnou navigáciou, focus ringom
 * a aktívnym krokom), kde sa smie objaviť brand farba — preto sa text berie
 * z `--color-on-brand`, ktorý je odvodený, nie zvolený.
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

const props = withDefaults(
	defineProps<{
		variant?: Variant
		size?: ControlSize
		type?: 'button' | 'submit' | 'reset'
		disabled?: boolean
		/** Počas behu akcie blokuje opakovaný klik a hlási sa cez `aria-busy`. */
		loading?: boolean
		/** Povinné pri tlačidle bez textu — inak ho čítačka prečíta ako „button“. */
		accessibleNameSk?: string
		/** Roztiahne tlačidlo na celú šírku (mobilné CTA, akcie v dialógu). */
		block?: boolean
	}>(),
	{
		variant: 'secondary',
		size: 'md',
		type: 'button',
		accessibleNameSk: undefined,
	},
)

const VARIANT: Readonly<Record<Variant, string>> = {
	primary: 'bg-brand text-on-brand border-brand hover:opacity-90 active:opacity-100',
	secondary: 'bg-surface text-fg border-border hover:bg-surface-muted hover:border-border-strong',
	ghost: 'bg-transparent text-fg-muted border-transparent hover:bg-surface-muted hover:text-fg',
	// text-on-danger, nie text-surface: surface sa v tmavom režime preklopí na
	// tmavú a kontrast na červenej klesne pod hranicu AA.
	danger: 'bg-danger text-on-danger border-danger hover:opacity-90',
}

const isBlocked = computed(() => props.disabled === true || props.loading === true)

const classes = computed(() =>
	cn(
		'inline-flex items-center justify-center gap-2 rounded-md border font-medium',
		'select-none whitespace-nowrap',
		// Pohyb len cez opacity — width/height/top/left sú zakázané (MASTER.md §5).
		'transition-opacity duration-(--motion-fast) ease-out',
		'focus-visible:focus-ring',
		'disabled:cursor-not-allowed disabled:opacity-50',
		CONTROL_HEIGHT[props.size],
		CONTROL_PADDING[props.size],
		VARIANT[props.variant],
		props.block === true ? 'w-full' : '',
	),
)
</script>

<template>
	<button
		:type="props.type"
		:class="classes"
		:disabled="isBlocked"
		:aria-label="props.accessibleNameSk"
		:aria-busy="props.loading === true ? 'true' : undefined"
	>
		<LoaderCircle
			v-if="props.loading === true"
			class="animate-spin"
			:size="16"
			:stroke-width="ICON_STROKE"
			aria-hidden="true"
		/>
		<slot />
	</button>
</template>
